import { rd } from '@passionware/monads';
import { createMatcher } from '@passionware/query-toolkit';
import React, { Component } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid/index';
import {
  EmptyGeneric,
  Loading,
  MultiMediaUploader,
  PinItem
} from 'v1/components/shared';
import { chain, isEmpty, uniq } from 'lodash';

import {
  findGalleryPinForProfilePin,
  isProfilePinInGallery
} from '../isProfilePinInGallery';
import { PlusIcon } from 'lucide-react';
import { Button } from 'v5/design-system/Button';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectQueryRenderer,
  MultiSelectTrigger
} from 'v5/design-system/MultiSelect';
import { SimpleTooltip } from 'v5/design-system/Tooltip';

import './MediaFromProfilePins.scss';
import { cn } from 'v5/platform/dom/cn';

export default class MediaFromProfilePins extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeProfileTab: 'pins',
      suggestibleTags: this.buildSuggestibleTags(props),
      tags: []
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.pins !== this.props.pins ||
      prevProps.contact !== this.props.contact
    ) {
      this.setState({
        suggestibleTags: this.buildSuggestibleTags(this.props)
      });
    }
  }
  buildSuggestibleTags = props => {
    const pins = props.pins;
    if (isEmpty(pins)) return [];
    return uniq(
      pins
        .filter(p => !p.archived)
        .flatMap(p =>
          (p.tags || []).map(t => (typeof t === 'string' ? t : t?.tag))
        )
        .filter(Boolean)
    );
  };
  filterPins = pins => {
    const tags = this.state.tags;
    let filteredPins = [];
    pins = chain(pins)
      .filter(p => !p.archived)
      .orderBy(['order', 'asc'])
      .value();
    pins.map(pin => {
      const pinTagsStringArray = (pin.tags || []).map(t =>
        typeof t === 'string' ? t : t.tag
      );
      const isMatch = tags.every(i => pinTagsStringArray.includes(i));
      return isMatch ? filteredPins.push(pin) : null;
    });
    return filteredPins;
  };

  handleUpload = (path, data) => {
    const { contact } = this.props;
    let toSubmit;

    switch (path) {
      case 'URL':
        let pin_type = 'external_link';
        switch (data.provider) {
          case 'YOUTUBE':
            pin_type = 'youtube/video';
            break;
          case 'VIMEO':
            pin_type = 'vimeo/video';
            break;
          case 'IMAGE':
            pin_type = 'external_image_link';
            break;
          case 'EXTERNAL_URL':
          default:
            pin_type = 'external_link';
        }
        toSubmit = { url: data.url, pin_type };
        break;
      case 'FILE':
        toSubmit = {
          url: data.url,
          pin_type: 'file',
          file_id: data.file_id
        };
        break;
      case 'GOOGLE':
        toSubmit = {
          type: `google/${data.mimeType}`,
          file: {
            url: data.url,
            size: data.sizeBytes,
            name: data.name,
            mime_type: data.mimeType,
            type: 'google'
          }
        };
        break;
      default:
        return;
    }
    this.props.createContactPin(toSubmit, contact.id);
  };

  handleGalleryToggle = (profilePin, inGallery) => {
    const { gallery = [], onSelect, onRemoveFromGallery } = this.props;
    if (inGallery) {
      const galleryPin = findGalleryPinForProfilePin(profilePin, gallery);
      if (galleryPin && onRemoveFromGallery) {
        onRemoveFromGallery(galleryPin);
      }
      return;
    }
    onSelect(profilePin);
  };

  renderToolbar = () => {
    const { contacts } = this.props;
    return (
      <div className="mb-1.5 flex shrink-0 flex-wrap items-end justify-between gap-x-3 gap-y-2 pb-1.5">
        {!isEmpty(this.props.pins) && (
          <div className="min-w-0 flex-1 pr-2">
            <MultiSelect
              value={this.state.tags}
              onValueChange={tags => this.setState({ tags })}
            >
              <MultiSelectTrigger
                variant="inline"
                size="sm"
                placeholder="Search tagged pins"
              >
                {values => rd.of(values.map(v => v))}
              </MultiSelectTrigger>
              <MultiSelectContent shouldFilter={false}>
                <MultiSelectQueryRenderer
                  children={query =>
                    rd.of(
                      <MultiSelectGroup>
                        {this.state.suggestibleTags
                          .filter(createMatcher(query, x => x))
                          .map(tag => (
                            <MultiSelectItem key={tag} value={tag}>
                              {tag}
                            </MultiSelectItem>
                          ))}
                      </MultiSelectGroup>
                    )
                  }
                />
              </MultiSelectContent>
            </MultiSelect>
          </div>
        )}
        <MultiMediaUploader
          title="Add Pins"
          buttonLabel="Add Pins"
          showIcon
          uploadMethods={['computer', 'url']}
          onUpload={this.handleUpload}
          onFileCompleted={file => this.handleUpload('FILE', file)}
          uploading={contacts.loading === 'CREATE_CONTACT_PIN'}
          secure={false}
          className="ml-auto"
        />
      </div>
    );
  };

  render() {
    const { contact, pins, profileHref, gallery = [] } = this.props;

    if (!contact) return <Loading />;

    const filteredPins = this.filterPins(pins);

    return (
      <div className="MediaFromProfilePins flex h-full min-h-0 min-w-0 flex-1 flex-col">
        {this.renderToolbar()}
        <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
          {!isEmpty(pins) ? (
            <div className="MediaFromProfilePins-items flex w-full min-w-0 flex-col gap-px">
              {filteredPins.map((pin, index) => {
                const inGallery = isProfilePinInGallery(pin, gallery);
                const label = inGallery
                  ? 'Remove from gallery'
                  : 'Add to gallery';
                return (
                  <div
                    key={`pin-${pin.id}-${index}`}
                    className="MediaFromProfilePins-item relative min-w-0 overflow-hidden rounded"
                  >
                    {/* Pin preview is decorative; hits go to the v5 headless overlay. */}
                    <div className="pointer-events-none relative z-0 overflow-hidden rounded-[inherit]">
                      <PinItem
                        pin={pin}
                        settings={{
                          video: {
                            showPreview: true,
                            disableControls: true
                          }
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="headless"
                      aria-pressed={inGallery}
                      tabIndex={-1}
                      aria-label={label}
                      className={
                        // Default DS size applies fixed height — stretch to tile.
                        'absolute inset-0 z-[1] !h-full !min-h-0 w-full min-w-0 rounded-[inherit] p-0'
                      }
                      onClick={() => this.handleGalleryToggle(pin, inGallery)}
                    />
                    <SimpleTooltip title={label} side="top" delayDuration={300}>
                      <Button
                        variant={inGallery ? 'success' : 'invertAlt'}
                        shape="rounded"
                        layout="icon"
                        size="md"
                        className={cn('absolute right-2 top-2 z-[2] shadow-sm')}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          this.handleGalleryToggle(pin, inGallery);
                        }}
                      >
                        {inGallery ? (
                          <CheckIcon className="size-6! " />
                        ) : (
                          <PlusIcon className="size-6! " />
                        )}
                      </Button>
                    </SimpleTooltip>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyGeneric
              icon="/images/icon_colour_pins.svg"
              iconWidth="30px"
              title={`Nothing Pinned on ${contact.first_name}'s Profile`}
              description="You can build a pinboard within a persons profile page or clicking 'Add Pins'"
              action={{
                text: `Go to Profile`,
                className: 'btn btn-default',
                href: profileHref,
                target: '_blank'
              }}
            />
          )}
        </div>
      </div>
    );
  }
}
